import { useState, useEffect, useRef } from "react";
import { useAppTheme, NAVY, ACCENT, useIsMobile } from "./ThemeContext";
import { useAuth } from "./AuthContext";

const API = "/api";

export default function UserManager() {
  const themeCtx = useAppTheme();
  const { user: currentUser } = useAuth() || {};
  const mobile = useIsMobile();
  const dark = themeCtx ? themeCtx.dark : true;
  const t = themeCtx ? themeCtx.t : (dark ? require("./ThemeContext").THEMES.dark : require("./ThemeContext").THEMES.light);

  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", phone_number: "", email: "", role: "operator", site_ids: [] });
  const [error, setError] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const menuRef = useRef(null);

  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(null); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const loadUsers = () => {
    fetch(`${API}/auth/users`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(setUsers)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();
    fetch(`${API}/sites`).then(r => r.json()).then(setSites);
  }, []);

  const handleSave = async () => {
    setError(null);
    const body = { ...form, site_ids: form.site_ids.map(Number) };
    if (!body.name.trim()) { setError("Nimi on kohustuslik"); return; }
    try {
      const url = editId ? `${API}/auth/users/${editId}` : `${API}/auth/users`;
      const method = editId ? "PATCH" : "POST";
      const res = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Viga"); }
      setShowAdd(false);
      setEditId(null);
      setForm({ name: "", phone_number: "", email: "", role: "operator", site_ids: [] });
      loadUsers();
    } catch (e) { setError(e.message); }
  };

  const toggleActive = async (u) => {
    await fetch(`${API}/auth/users/${u.id}`, {
      method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ active: !u.active }),
    });
    loadUsers();
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Kustuta kasutaja "${u.name}"? Seda ei saa tagasi võtta.`)) return;
    const res = await fetch(`${API}/auth/users/${u.id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) { const d = await res.json(); alert(d.detail || "Viga"); return; }
    loadUsers();
  };

  const startEdit = (u) => {
    setEditId(u.id);
    setShowAdd(true);
    setForm({
      name: u.name || "", phone_number: u.phone_number || "", email: u.email || "",
      role: u.role || "operator", site_ids: (u.sites || []).map(s => String(s.id)),
    });
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "10px 12px", fontSize: 14,
    border: `1px solid ${t.border}`, borderRadius: 8, background: t.bg,
    color: t.text, fontFamily: "inherit",
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: t.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 };

  const roleLabels = { admin: "Admin", site_lead: "Objektijuht", operator: "Operaator" };

  return (
    <div style={{ padding: mobile ? 16 : 28, maxWidth: 900, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: mobile ? 18 : 22, fontWeight: 700, color: t.text }}>Kasutajad</div>
          <div style={{ fontSize: 12.5, color: t.muted }}>{users.length} kasutajat</div>
        </div>
        {isAdmin && (
          <button onClick={() => { setShowAdd(true); setEditId(null); setForm({ name: "", phone_number: "", email: "", role: "operator", site_ids: [] }); }}
            style={{ padding: "10px 18px", background: ACCENT, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            + Lisa kasutaja
          </button>
        )}
      </div>

      {showAdd && (
        <div style={{ background: t.panel, border: `1px solid ${t.border}`, borderRadius: 12, padding: mobile ? 16 : 24, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: t.text, marginBottom: 16 }}>
            {editId ? "Muuda kasutajat" : "Uus kasutaja"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 14 }}>
            <div>
              <div style={labelStyle}>Nimi</div>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>Telefon</div>
              <input value={form.phone_number} onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))} placeholder="+372..." style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>E-post</div>
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <div style={labelStyle}>Roll</div>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                disabled={!isAdmin} style={{ ...inputStyle, appearance: "auto" }}>
                <option value="operator">Operaator</option>
                <option value="site_lead">Objektijuht</option>
                {isAdmin && <option value="admin">Admin</option>}
              </select>
            </div>
            <div>
              <div style={labelStyle}>Objektid</div>
              <div style={{ ...inputStyle, padding: "6px 12px", display: "flex", flexDirection: "column", gap: 4, maxHeight: 120, overflowY: "auto" }}>
                {sites.map(s => (
                  <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: t.text }}>
                    <input type="checkbox" checked={form.site_ids.includes(String(s.id))}
                      onChange={e => {
                        const sid = String(s.id);
                        setForm(f => ({ ...f, site_ids: e.target.checked ? [...f.site_ids, sid] : f.site_ids.filter(x => x !== sid) }));
                      }} />
                    {s.name}
                  </label>
                ))}
                {sites.length === 0 && <span style={{ color: t.muted, fontSize: 12 }}>Objektid puuduvad</span>}
              </div>
            </div>
          </div>
          {error && <div style={{ color: t.error, fontSize: 12.5, marginTop: 10 }}>{error}</div>}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button onClick={handleSave} style={{ padding: "10px 20px", background: ACCENT, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {editId ? "Salvesta" : "Lisa"}
            </button>
            <button onClick={() => { setShowAdd(false); setEditId(null); setError(null); }}
              style={{ padding: "10px 20px", background: "transparent", color: t.muted, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>
              Tühista
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: t.muted, textAlign: "center", padding: 40, fontSize: 13 }}>Laadin...</div>
      ) : (
        <div style={{ background: t.panel, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: mobile ? 12 : 13 }}>
              <thead>
                <tr style={{ background: NAVY }}>
                  <th style={thStyle}>Nimi</th>
                  {!mobile && <th style={thStyle}>Telefon</th>}
                  {!mobile && <th style={thStyle}>E-post</th>}
                  <th style={thStyle}>Roll</th>
                  <th style={thStyle}>Objekt</th>
                  <th style={thStyle}>Staatus</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom: `1px solid ${t.rowBorder}` }}>
                    <td style={tdStyle(t)}>{u.name}</td>
                    {!mobile && <td style={tdStyle(t)}>{u.phone_number || "—"}</td>}
                    {!mobile && <td style={tdStyle(t)}>{u.email || "—"}</td>}
                    <td style={tdStyle(t)}>
                      <span style={{
                        fontSize: 10.5, fontWeight: 600, padding: "3px 8px", borderRadius: 4,
                        background: u.role === "admin" ? "#fef3c7" : u.role === "site_lead" ? "#dbeafe" : "#d1fae5",
                        color: u.role === "admin" ? "#92400e" : u.role === "site_lead" ? "#1e40af" : "#065f46",
                      }}>{roleLabels[u.role] || u.role}</span>
                    </td>
                    <td style={tdStyle(t)}>{u.sites?.length ? u.sites.map(s => s.name).join(", ") : "—"}</td>
                    <td style={tdStyle(t)}>
                      <span style={{ color: u.active ? "#22c55e" : t.muted, fontSize: 11.5, fontWeight: 600 }}>
                        {u.active ? "Aktiivne" : "Keelatud"}
                      </span>
                    </td>
                    <td style={{ ...tdStyle(t), position: "relative", textAlign: "right" }}
                      ref={menuOpen === u.id ? menuRef : undefined}>
                      <button onClick={() => setMenuOpen(menuOpen === u.id ? null : u.id)}
                        style={{ background: "none", border: "none", color: t.muted, fontSize: 18, cursor: "pointer", padding: "4px 8px", lineHeight: 1 }}>
                        &#8942;
                      </button>
                      {menuOpen === u.id && (
                        <div style={{
                          position: "absolute", right: 12, top: "100%", zIndex: 20,
                          background: t.panel, border: `1px solid ${t.border}`, borderRadius: 8,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)", minWidth: 140, overflow: "hidden",
                        }}>
                          <button onClick={() => { setMenuOpen(null); startEdit(u); }}
                            style={menuItemStyle(t)}>Muuda</button>
                          {u.id !== currentUser?.id && (
                            <button onClick={() => { setMenuOpen(null); toggleActive(u); }}
                              style={{ ...menuItemStyle(t), color: u.active ? t.error : "#22c55e" }}>
                              {u.active ? "Keela" : "Aktiveeri"}
                            </button>
                          )}
                          {isAdmin && u.id !== currentUser?.id && (
                            <button onClick={() => { setMenuOpen(null); deleteUser(u); }}
                              style={{ ...menuItemStyle(t), color: "#ef4444" }}>
                              Kustuta
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = { textAlign: "left", padding: "8px 12px", fontWeight: 600, color: "#fff", fontSize: 11, whiteSpace: "nowrap" };
const tdStyle = (t) => ({ padding: "10px 12px", color: t.text, verticalAlign: "middle" });
const menuItemStyle = (t) => ({
  display: "block", width: "100%", padding: "10px 16px", background: "none", border: "none",
  fontSize: 13, color: t.text, cursor: "pointer", textAlign: "left", whiteSpace: "nowrap",
});
